class YuyaSetConfigurator {
  constructor(element) {
    this.element = element;

    if (this.element.dataset.yuyaInitialized === 'true') return;
    this.element.dataset.yuyaInitialized = 'true';

    this.groups = Array.from(
      this.element.querySelectorAll('[data-yuya-set-group]')
    );

    this.totalElement = this.element.querySelector(
      '[data-yuya-set-total]'
    );

    this.statusElement = this.element.querySelector(
      '[data-yuya-set-status]'
    );

    this.basePrice = this.toNumber(
      this.element.dataset.mainPrice,
      0
    );

    this.mainVariantId =
      this.element.dataset.mainVariantId;

    this.successMessage =
      this.element.dataset.successMessage ||
      'Dein Set wurde zum Warenkorb hinzugefügt.';

    this.currency =
      window.Shopify?.currency?.active ||
      document.documentElement.dataset.currency ||
      'EUR';

    this.initializeQuantityControls();
    this.bindEvents();
    this.update();
  }

  toNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  getGroupInputs(group) {
    return Array.from(
      group.querySelectorAll('[data-yuya-group-option]')
    );
  }

  getSelectedInputs(group) {
    return this.getGroupInputs(group).filter(
      (input) => input.checked && input.value
    );
  }

  getAllSelectedOptions() {
    return this.groups.flatMap((group) =>
      this.getSelectedInputs(group)
    );
  }

  isQuantityGroup(group) {
    return group?.dataset?.quantityEnabled === 'true';
  }

  getInputQuantity(input) {
    if (!input?.checked || !input.value) return 0;

    const group = input.closest('[data-yuya-set-group]');
    if (!this.isQuantityGroup(group)) return 1;

    return Math.max(
      1,
      this.toNumber(input.dataset.yuyaQuantity, 1)
    );
  }

  setInputQuantity(input, quantity) {
    const group = input.closest('[data-yuya-set-group]');
    const normalized = Math.max(0, Math.floor(this.toNumber(quantity, 0)));

    if (!this.isQuantityGroup(group)) {
      input.checked = normalized > 0;
      return;
    }

    input.dataset.yuyaQuantity = String(normalized);
    input.checked = normalized > 0;
    this.updateQuantityUI(input);
  }

  getGroupSelectedQuantity(group) {
    return this.getGroupInputs(group).reduce(
      (sum, input) => sum + this.getInputQuantity(input),
      0
    );
  }

  getInventoryLimit(input) {
    const raw = input?.dataset?.inventoryLimit;

    if (raw === undefined || raw === null || raw === '') {
      return Infinity;
    }

    return Math.max(0, this.toNumber(raw, 0));
  }

  initializeQuantityControls() {
    this.groups.forEach((group) => {
      if (!this.isQuantityGroup(group)) return;

      this.getGroupInputs(group).forEach((input) => {
        if (
          input.type !== 'checkbox' ||
          !input.value ||
          !input.dataset.variantId
        ) {
          return;
        }

        input.dataset.yuyaQuantity = input.checked ? '1' : '0';

        const option = input.closest('.yuya-set-option');
        if (!option) return;

        option.classList.add('is-quantity-enabled');

        const image = option.querySelector('.yuya-set-option__image');
        const host = image || option;

        if (!option.querySelector('[data-yuya-quantity-control]')) {
          const control = document.createElement('span');
          control.className = 'yuya-set-option__quantity';
          control.dataset.yuyaQuantityControl = '';
          control.hidden = true;
          control.innerHTML = `
            <button type="button" data-yuya-qty-minus aria-label="Menge verringern">−</button>
            <span class="yuya-set-option__quantity-value" data-yuya-qty-value>1</span>
            <button type="button" data-yuya-qty-plus aria-label="Menge erhöhen">+</button>
          `;
          host.appendChild(control);
        }

        if (!option.querySelector('[data-yuya-stock-message]')) {
          const message = document.createElement('span');
          message.className = 'yuya-set-option__stock-message';
          message.dataset.yuyaStockMessage = '';
          message.hidden = true;
          host.appendChild(message);
        }

        this.updateQuantityUI(input);
      });
    });
  }

  updateQuantityUI(input) {
    const option = input.closest('.yuya-set-option');
    if (!option) return;

    const quantity = this.getInputQuantity(input);
    const control = option.querySelector('[data-yuya-quantity-control]');
    const value = option.querySelector('[data-yuya-qty-value]');

    if (control) control.hidden = quantity <= 0;
    if (value) value.textContent = String(Math.max(1, quantity));
  }

  showStockLimit(input, limit) {
    const option = input.closest('.yuya-set-option');
    const message = option?.querySelector('[data-yuya-stock-message]');
    if (!message) return;

    const safeLimit = Math.max(0, Math.floor(this.toNumber(limit, 0)));

    message.textContent =
      safeLimit === 1
        ? 'Nur noch 1 verfügbar'
        : `Nur noch ${safeLimit} verfügbar`;

    message.hidden = false;

    window.clearTimeout(option._yuyaStockMessageTimer);
    option._yuyaStockMessageTimer = window.setTimeout(() => {
      message.hidden = true;
    }, 1800);
  }

  flashGroupLimit(group, input) {
    const max = this.getGroupMax(group);

    this.showGroupError(
      group,
      `Du kannst maximal ${max} auswählen.`
    );

    const option = input.closest('.yuya-set-option');
    option?.classList.add('is-limit-reached');

    window.setTimeout(() => {
      option?.classList.remove('is-limit-reached');
    }, 500);
  }

  adjustQuantity(group, input, delta) {
    if (!this.isQuantityGroup(group) || input.disabled) return;

    const current = this.getInputQuantity(input);
    const groupTotal = this.getGroupSelectedQuantity(group);
    const groupMax = this.getGroupMax(group);
    const inventoryLimit = this.getInventoryLimit(input);

    if (delta > 0) {
      if (groupMax > 0 && groupTotal >= groupMax) {
        this.flashGroupLimit(group, input);
        return;
      }

      if (Number.isFinite(inventoryLimit) && current >= inventoryLimit) {
        this.showStockLimit(input, inventoryLimit);
        return;
      }

      this.setInputQuantity(input, current + 1);
    } else if (delta < 0) {
      this.setInputQuantity(input, Math.max(0, current - 1));
    }

    this.hideGroupError(group);
    this.clearStatus();
    this.update();
  }

  getTextInput(group) {
    return group.querySelector('[data-yuya-text-input]');
  }

  getFileInput(group) {
    return group.querySelector('[data-yuya-file-input]');
  }

  getInputGroupValue(group) {
    const textInput = this.getTextInput(group);
    if (textInput) return textInput.value.trim();

    const fileInput = this.getFileInput(group);
    if (fileInput) return fileInput.files?.[0] || null;

    return null;
  }

  getGroupMin(group) {
    return this.toNumber(group.dataset.min, 0);
  }

  getGroupMax(group) {
    return this.toNumber(
      group.dataset.max,
      this.getGroupMin(group)
    );
  }

  bindEvents() {
    this.groups.forEach((group) => {
      this.getGroupInputs(group).forEach((input) => {
        input.addEventListener('change', (event) => {
          this.handleGroupChange(group, event);
        });
      });

      if (this.isQuantityGroup(group)) {
        group.addEventListener('click', (event) => {
          const option = event.target.closest('.yuya-set-option');
          if (!option || !group.contains(option)) return;

          const input = option.querySelector('[data-yuya-group-option]');
          if (
            !input ||
            input.type !== 'checkbox' ||
            !input.dataset.variantId
          ) {
            return;
          }

          const minus = event.target.closest('[data-yuya-qty-minus]');
          const plus = event.target.closest('[data-yuya-qty-plus]');

          event.preventDefault();
          event.stopPropagation();

          if (minus) {
            this.adjustQuantity(group, input, -1);
            return;
          }

          if (plus) {
            this.adjustQuantity(group, input, 1);
            return;
          }

          this.adjustQuantity(group, input, 1);
        });
      }

      const textInput = this.getTextInput(group);
      if (textInput) {
        textInput.addEventListener('input', () => {
          this.hideGroupError(group);
          this.clearStatus();
        });
      }

      const fileInput = this.getFileInput(group);
      if (fileInput) {
        fileInput.addEventListener('change', () => {
          const fileName = group.querySelector('[data-yuya-file-name]');
          if (fileName) {
            const selectedName = fileInput.files?.[0]?.name || '';
            fileName.textContent = selectedName;
            fileName.hidden = !selectedName;
          }
          this.hideGroupError(group);
          this.clearStatus();
        });
      }
    });
  }

  handleGroupChange(group, event) {
    const input = event.currentTarget;
    const max = this.getGroupMax(group);

    if (this.isQuantityGroup(group)) {
      if (input.checked) {
        if (this.toNumber(input.dataset.yuyaQuantity, 0) < 1) {
          input.dataset.yuyaQuantity = '1';
        }
      } else {
        input.dataset.yuyaQuantity = '0';
      }

      if (max > 0 && this.getGroupSelectedQuantity(group) > max) {
        this.setInputQuantity(input, 0);
        this.flashGroupLimit(group, input);
      } else {
        this.hideGroupError(group);
      }

      this.clearStatus();
      this.update();
      return;
    }

    const selected = this.getSelectedInputs(group);

    if (
      input.type === 'checkbox' &&
      input.checked &&
      max > 0 &&
      selected.length > max
    ) {
      input.checked = false;
      this.flashGroupLimit(group, input);
    } else {
      this.hideGroupError(group);
    }

    this.clearStatus();
    this.update();
  }

  update() {
    this.groups.forEach((group) => {
      this.updateGroupCounter(group);
      this.updateGroupAvailability(group);
    });

    this.updateTotal();
  }

  updateGroupCounter(group) {
    const counter = group.querySelector(
      '[data-yuya-group-counter]'
    );

    if (!counter) return;

    const count = this.isQuantityGroup(group)
      ? this.getGroupSelectedQuantity(group)
      : this.getSelectedInputs(group).length;

    const min = this.getGroupMin(group);
    const max = this.getGroupMax(group);

    if (min === max) {
      counter.textContent =
        `${count} von ${min} ausgewählt`;
    } else {
      counter.textContent =
        `${count} von mindestens ${min} ausgewählt`;
    }

    counter.classList.toggle(
      'is-complete',
      count >= min && (max <= 0 || count <= max)
    );
  }

  updateGroupAvailability(group) {
    const inputs = this.getGroupInputs(group);
    const max = this.getGroupMax(group);

    if (max <= 0) return;

    const selectedCount = this.isQuantityGroup(group)
      ? this.getGroupSelectedQuantity(group)
      : this.getSelectedInputs(group).length;

    const maximumReached = selectedCount >= max;

    inputs.forEach((input) => {
      const option = input.closest('.yuya-set-option');

      if (!option || input.disabled) return;

      option.classList.toggle(
        'is-maximum-reached',
        input.type === 'checkbox' &&
          maximumReached &&
          !input.checked
      );

      if (this.isQuantityGroup(group)) {
        this.updateQuantityUI(input);
      }
    });
  }

  updateTotal() {
    if (!this.totalElement) return;

    const addonsTotal =
      this.getAllSelectedOptions().reduce(
        (sum, input) => {
          const optionQuantity = this.getInputQuantity(input);

          return (
            sum +
            (
              this.toNumber(input.dataset.price, 0) +
              this.toNumber(input.dataset.surchargePrice, 0)
            ) * optionQuantity
          );
        },
        0
      );

    this.totalElement.textContent =
      this.formatMoney(this.basePrice + addonsTotal);
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'de-DE',
        {
          style: 'currency',
          currency: this.currency
        }
      ).format(cents / 100);
    } catch (error) {
      return `${(cents / 100)
        .toFixed(2)
        .replace('.', ',')} €`;
    }
  }

  validate() {
    let firstInvalidGroup = null;

    for (const group of this.groups) {
      const title = group.dataset.groupTitle || 'Diese Auswahl';
      const required = group.dataset.required === 'true';
      const sourceType = group.dataset.sourceType || '';

      if (sourceType === 'Textfeld') {
        const value = this.getTextInput(group)?.value.trim() || '';
        if (required && !value) {
          this.showGroupError(group, `${title}: Bitte fülle dieses Feld aus.`);
          firstInvalidGroup ||= group;
          continue;
        }
        this.hideGroupError(group);
        continue;
      }

      if (sourceType === 'Datei-Upload') {
        const file = this.getFileInput(group)?.files?.[0] || null;
        if (required && !file) {
          this.showGroupError(group, `${title}: Bitte lade eine Datei hoch.`);
          firstInvalidGroup ||= group;
          continue;
        }
        this.hideGroupError(group);
        continue;
      }

      const count = this.isQuantityGroup(group)
        ? this.getGroupSelectedQuantity(group)
        : this.getSelectedInputs(group).length;
      const min = this.getGroupMin(group);
      const max = this.getGroupMax(group);

      if (count < min) {
        this.showGroupError(
          group,
          `${title}: Bitte wähle mindestens ${min} aus.`
        );
        firstInvalidGroup ||= group;
        continue;
      }

      if (max > 0 && count > max) {
        this.showGroupError(
          group,
          `${title}: Du kannst maximal ${max} auswählen.`
        );
        firstInvalidGroup ||= group;
        continue;
      }

      this.hideGroupError(group);
    }

    if (firstInvalidGroup) {
      firstInvalidGroup.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return false;
    }

    return true;
  }

  showGroupError(group, message) {
    const error = group.querySelector(
      '[data-yuya-group-error]'
    );

    if (!error) return;

    error.textContent = message;
    error.hidden = false;
  }

  hideGroupError(group) {
    const error = group.querySelector(
      '[data-yuya-group-error]'
    );

    if (error) error.hidden = true;
  }

  showStatus(message, type = '') {
    if (!this.statusElement) return;

    this.statusElement.textContent = message;
    this.statusElement.hidden = false;

    this.statusElement.classList.remove(
      'is-success',
      'is-error'
    );

    if (type) {
      this.statusElement.classList.add(
        `is-${type}`
      );
    }
  }

  clearStatus() {
    if (!this.statusElement) return;

    this.statusElement.textContent = '';
    this.statusElement.hidden = true;

    this.statusElement.classList.remove(
      'is-success',
      'is-error'
    );
  }

  findProductForm(submittedForm = null) {
    if (submittedForm) return submittedForm;

    const productInfo =
      this.element.closest('product-info');

    if (productInfo) {
      return productInfo.querySelector(
        'form[action*="/cart/add"]'
      );
    }

    const section =
      this.element.closest('.shopify-section');

    return section?.querySelector(
      'form[action*="/cart/add"]'
    );
  }

  getCurrentMainVariantId(form) {
    const variantInput = form?.querySelector(
      '[name="id"]'
    );

    return (
      variantInput?.value ||
      this.mainVariantId
    );
  }

  getMainQuantity(form) {
    const quantityInput = form?.querySelector(
      '[name="quantity"]'
    );

    const quantity = this.toNumber(
      quantityInput?.value,
      1
    );

    return Math.max(1, quantity);
  }

  getMainProperties(form) {
    const properties = {};

    if (!form) return properties;

    const formData = new FormData(form);

    for (const [name, value] of formData.entries()) {
      const match =
        name.match(/^properties\[(.+)\]$/);

      if (
        match &&
        !(value instanceof File) &&
        value !== null &&
        String(value).trim() !== ''
      ) {
        properties[match[1]] = String(value);
      }
    }

    return properties;
  }

  createBundleId() {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID === 'function'
    ) {
      return window.crypto.randomUUID();
    }

    return [
      'yuya',
      Date.now(),
      Math.random().toString(36).slice(2)
    ].join('-');
  }

  buildCartItems(form) {
    const mainVariantId = this.getCurrentMainVariantId(form);
    const quantity = this.getMainQuantity(form);
    const bundleId = this.createBundleId();
    const mainProperties = this.getMainProperties(form);

    mainProperties._yuya_bundle_id = bundleId;
    mainProperties._yuya_is_main_product = 'true';

    this.groups.forEach((group) => {
      const title = group.dataset.groupTitle || 'Auswahl';
      const sourceType = group.dataset.sourceType || '';

      if (sourceType === 'Textfeld') {
        const value = this.getTextInput(group)?.value.trim() || '';
        if (value) mainProperties[title] = value;
        return;
      }

      // Produkt-/Garn-/Modell-Auswahlen sind eigene Warenkorbpositionen.
      // Deshalb nicht zusätzlich am Basis-Set ausgeben.
    });

    const items = [{
      id: Number(mainVariantId),
      quantity,
      properties: mainProperties
    }];

    this.groups.forEach((group) => {
      const groupTitle = group.dataset.groupTitle || 'Auswahl';

      this.getSelectedInputs(group).forEach((input) => {
        const optionKind = input.dataset.optionKind || 'product';

        if (optionKind !== 'model') {
          const variantId = this.toNumber(
            input.dataset.variantId || input.value, 0
          );
          if (variantId > 0) {
            const optionQuantity = Math.max(
              1,
              this.getInputQuantity(input)
            );

            items.push({
              id: variantId,
              quantity: quantity * optionQuantity,
              properties: {
                _yuya_bundle_id: bundleId,
                _yuya_addon_type: 'set_option',
                _yuya_group_title: groupTitle,
                _yuya_main_product_id: this.element.dataset.mainProductId || ''
              }
            });
          }
        }

        const surchargeVariantId = this.toNumber(
          input.dataset.surchargeVariantId, 0
        );
        const surchargePrice = this.toNumber(
          input.dataset.surchargePrice, 0
        );

        if (surchargeVariantId > 0) {
          items.push({
            id: surchargeVariantId,
            quantity: quantity * Math.max(1, this.getInputQuantity(input)),
            properties: {
              Modell: input.dataset.title || '',
              _yuya_bundle_id: bundleId,
              _yuya_addon_type: 'model_surcharge',
              _yuya_group_title: groupTitle,
              _yuya_option_title: input.dataset.title || '',
              _yuya_option_image: input.dataset.modelImage || '',
              _yuya_main_product_id: this.element.dataset.mainProductId || ''
            }
          });
        }
      });
    });

    return items;
  }

  getSelectedFileEntries() {
    return this.groups
      .map((group) => {
        const input = this.getFileInput(group);
        const file = input?.files?.[0] || null;
        if (!file) return null;

        return {
          title: group.dataset.groupTitle || 'Datei',
          file
        };
      })
      .filter(Boolean);
  }

  async addMainItemWithFiles(form, mainItem, fileEntries, root) {
    const formData = new FormData();

    formData.append('id', String(mainItem.id));
    formData.append('quantity', String(mainItem.quantity));

    Object.entries(mainItem.properties || {}).forEach(([key, value]) => {
      formData.append(`properties[${key}]`, String(value));
    });

    fileEntries.forEach(({ title, file }) => {
      formData.append(`properties[${title}]`, file, file.name);
    });

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.description ||
        result.message ||
        'Die Datei konnte nicht mit dem Set hochgeladen werden.'
      );
    }

    return result;
  }

  setLoading(form, loading) {
    const button = form?.querySelector(
      '[type="submit"][name="add"]'
    );

    if (!button) return;

    button.disabled = loading;
    button.classList.toggle(
      'loading',
      loading
    );

    button.setAttribute(
      'aria-busy',
      loading ? 'true' : 'false'
    );

    const spinner = button.querySelector(
      '.loading__spinner'
    );

    if (spinner) {
      spinner.classList.toggle(
        'hidden',
        !loading
      );
    }
  }

  async getCartSections(root, existingResult = null) {
    if (
      existingResult?.sections?.['cart-drawer'] ||
      existingResult?.sections?.['cart-icon-bubble']
    ) {
      return existingResult.sections;
    }

    const sectionResponse = await fetch(
      `${root}cart?sections=cart-drawer,cart-icon-bubble`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    if (!sectionResponse.ok) {
      throw new Error(
        'Der aktualisierte Warenkorb konnte nicht geladen werden.'
      );
    }

    return sectionResponse.json();
  }

  renderCartSections(sections) {
    if (!sections) return;

    const currentDrawer =
      document.querySelector('cart-drawer');

    const cartResult = { sections };

    /*
     * Wenn das Theme eine eigene Render-Methode bereitstellt,
     * verwenden wir genau diese. Dadurch bleiben interne
     * Drawer-Zustände und Event-Listener korrekt synchronisiert.
     */
    if (
      currentDrawer &&
      typeof currentDrawer.renderContents === 'function'
    ) {
      currentDrawer.renderContents(cartResult);
      return;
    }

    /*
     * Fallback für Themes ohne renderContents().
     */
    const drawerHtml = sections['cart-drawer'];
    const bubbleHtml = sections['cart-icon-bubble'];

    if (drawerHtml && currentDrawer) {
      const parser = new DOMParser();
      const drawerDoc = parser.parseFromString(
        drawerHtml,
        'text/html'
      );

      const freshDrawer =
        drawerDoc.querySelector('cart-drawer');

      if (freshDrawer) {
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        currentDrawer.classList.remove('is-empty');
      }
    }

    if (bubbleHtml) {
      const parser = new DOMParser();
      const bubbleDoc = parser.parseFromString(
        bubbleHtml,
        'text/html'
      );

      const freshBubble =
        bubbleDoc.querySelector('#cart-icon-bubble');
      const currentBubble =
        document.querySelector('#cart-icon-bubble');

      if (freshBubble && currentBubble) {
        currentBubble.innerHTML = freshBubble.innerHTML;
      }
    }
  }

  openCartDrawer() {
    window.setTimeout(() => {
      const drawer =
        document.querySelector('cart-drawer');

      if (!drawer) return;

      drawer.classList.remove('is-empty');

      if (typeof drawer.open === 'function') {
        drawer.open();
      } else {
        drawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      }
    }, 100);
  }

  async addToCart(form, redirectToCheckout = false) {
    if (!this.validate()) return;

    const items =
      this.buildCartItems(form);

    if (
      items.length === 0 ||
      !items[0].id
    ) {
      this.showStatus(
        'Das Set konnte nicht vorbereitet werden. Bitte lade die Seite neu.',
        'error'
      );
      return;
    }

    this.setLoading(form, true);
    this.clearStatus();

    const root =
      window.Shopify?.routes?.root || '/';

    const sections = [
      'cart-drawer',
      'cart-icon-bubble'
    ];

    try {
      const fileEntries = this.getSelectedFileEntries();
      let result;

      if (fileEntries.length > 0) {
        const [mainItem, ...addonItems] = items;

        result = await this.addMainItemWithFiles(
          form,
          mainItem,
          fileEntries,
          root
        );

        if (addonItems.length > 0) {
          const addonResponse = await fetch(
            `${root}cart/add.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              body: JSON.stringify({
                items: addonItems
              })
            }
          );

          const addonResult =
            await addonResponse.json();

          if (!addonResponse.ok) {
            throw new Error(
              addonResult.description ||
              addonResult.message ||
              'Die Set-Bestandteile konnten nicht hinzugefügt werden.'
            );
          }
        }
      } else {
        const response = await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url:
                window.location.pathname
            })
          }
        );

        result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            result.message ||
            'Das Set konnte nicht hinzugefügt werden.'
          );
        }
      }

      if (redirectToCheckout) {
        window.location.href = `${root}checkout`;
        return;
      }

      /*
       * Wichtig:
       * Bei normalen Sets liefert /cart/add.js die frisch gerenderten
       * Sections bereits in result.sections zurück. Diese verwenden wir
       * direkt, anstatt anschließend einen zweiten Refresh-Event-Flow
       * auszulösen, der den Drawer wieder in den alten Leerzustand setzen kann.
       *
       * Bei Sets mit Datei-Upload fehlen diese Sections im ersten Request.
       * Nur dann werden sie einmal frisch vom Cart geladen.
       */
      const freshSections =
        await this.getCartSections(root, result);

      this.renderCartSections(freshSections);
      this.openCartDrawer();

      this.showStatus(
        this.successMessage,
        'success'
      );

      document.dispatchEvent(
        new CustomEvent('yuya:set-added', {
          bubbles: true,
          detail: {
            items,
            response: result,
            sections: freshSections
          }
        })
      );
    } catch (error) {
      console.error(
        'YUYA Set Builder:',
        error
      );

      this.showStatus(
        error.message ||
          'Das Set konnte nicht hinzugefügt werden. Bitte versuche es erneut.',
        'error'
      );
    } finally {
      this.setLoading(form, false);
    }
  }

}

function initializeYuyaSetConfigurators(
  scope = document
) {
  const configurators =
    scope.querySelectorAll(
      '[data-yuya-set-configurator]'
    );

  configurators.forEach((element) => {
    if (!element.yuyaSetConfigurator) {
      element.yuyaSetConfigurator =
        new YuyaSetConfigurator(element);
    }
  });
}

function findConfiguratorForForm(form) {
  const productInfo =
    form.closest('product-info');

  if (productInfo) {
    const configurator =
      productInfo.querySelector(
        '[data-yuya-set-configurator]'
      );

    if (configurator) return configurator;
  }

  const section =
    form.closest('.shopify-section');

  return section?.querySelector(
    '[data-yuya-set-configurator]'
  );
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target.closest(
      'form[action*="/cart/add"]'
    );

    if (!form) return;

    const configurator =
      findConfiguratorForForm(form);

    if (!configurator) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const instance =
      configurator.yuyaSetConfigurator ||
      new YuyaSetConfigurator(
        configurator
      );

    configurator.yuyaSetConfigurator =
      instance;

    instance.addToCart(form);
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest('[data-yuya-direct-checkout]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const productForm = button.closest('product-form');
    const form = productForm?.querySelector(
      'form[action*="/cart/add"]'
    );

    if (!form) return;

    const configurator = findConfiguratorForForm(form);
    if (!configurator) return;

    const instance =
      configurator.yuyaSetConfigurator ||
      new YuyaSetConfigurator(configurator);

    configurator.yuyaSetConfigurator = instance;
    instance.addToCart(form, true);
  }
);

document.addEventListener(
  'DOMContentLoaded',
  () => {
    initializeYuyaSetConfigurators();
  }
);

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initializeYuyaSetConfigurators(
      event.target
    );
  }
);

document.addEventListener(
  'shopify:block:select',
  (event) => {
    initializeYuyaSetConfigurators(
      event.target
    );
  }
);

initializeYuyaSetConfigurators();